/**
 * Optional manual lines for Monthly Sale Report (Firestore).
 * Mfr. column: same as item brand — starts with OE → OE, otherwise ordinary.
 * `salesSpareway`: same shape as sales; merged only for Main — amounts subtract from Main branch sales (Spareway-bound).
 *
 * Storage (schemaVersion 2): root doc holds `overrides` + metadata; full upload rows live in
 * subcollection `lineChunks` (categories: sales per week, purchases, transferIn, transferOutPrev,
 * transferOutCurr, salesReturns, salesSpareway, expenses). Override fields mirror upload totals
 * for manual entry and legacy docs; report math prefers summed lines when present.
 */
import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { normalizeBranchValue } from "../config/appBranches";
import {
  MONTHLY_AUTOS_BRANDS,
  normalizeBrand,
  oeBrandFromBrandField,
} from "./monthlyAutosReportService";
import { parseIndirectExpenseAmount } from "../utils/parseIndirectExpenseSheet";
import {
  emptySalesByWeek,
  flattenSalesByWeek,
  parseWeekFromSalesCategory,
  parseWeekFromSparewayCategory,
  SALES_WEEKS_PER_MONTH,
  salesSparewayWeekCategory,
  salesWeekCategory,
} from "../utils/weeklySalesUpload";

const COLLECTION = "monthlySaleReportManual";
/** Large line arrays are stored in subdocs so the root doc stays under Firestore’s 1 MiB cap. */
const LINE_CHUNKS_SUB = "lineChunks";
const SCHEMA_VERSION_CHUNKED = 2;
/** Target max serialized size per chunk doc (bytes), under 1 MiB including overhead. */
const CHUNK_TARGET_BYTES = 700000;

function chunkRowsByApproxBytes(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const chunks = [];
  let current = [];
  let currentBytes = 0;
  for (const row of rows) {
    const rowBytes = JSON.stringify(row).length;
    if (current.length > 0 && currentBytes + rowBytes > CHUNK_TARGET_BYTES) {
      chunks.push(current);
      current = [row];
      currentBytes = rowBytes;
    } else {
      current.push(row);
      currentBytes += rowBytes;
    }
  }
  if (current.length) chunks.push(current);
  return chunks;
}

function chunkDocId(category, index) {
  return `${category}_${index}`;
}

async function deleteAllLineChunks(monthId) {
  const colRef = collection(db, COLLECTION, monthId, LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  const docs = snap.docs;
  for (let i = 0; i < docs.length; i += 500) {
    const batch = writeBatch(db);
    for (const d of docs.slice(i, i + 500)) {
      batch.delete(d.ref);
    }
    await batch.commit();
  }
}

function mergeChunkBuckets(buckets) {
  const keys = Object.keys(buckets)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b);
  return keys.flatMap((k) => buckets[k] || []);
}

function emptyWeekBucketMap() {
  const map = {};
  for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) map[w] = {};
  return map;
}

function mergeWeekBucketMap(weekBucketMap) {
  const byWeek = emptySalesByWeek();
  for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
    byWeek[w] = mergeChunkBuckets(weekBucketMap[w] || {});
  }
  return byWeek;
}

async function loadLineChunks(monthId) {
  const colRef = collection(db, COLLECTION, monthId, LINE_CHUNKS_SUB);
  const snap = await getDocs(colRef);
  const salesBuckets = {};
  const salesWeekBuckets = emptyWeekBucketMap();
  const purchasesBuckets = {};
  const transferInBuckets = {};
  const transferOutPrevBuckets = {};
  const transferOutCurrBuckets = {};
  const srBuckets = {};
  const salesSparewayBuckets = {};
  const salesSparewayWeekBuckets = emptyWeekBucketMap();
  const expensesBuckets = {};
  snap.forEach((cd) => {
    const c = cd.data() || {};
    const cat = c.category;
    const idx = Number.isFinite(Number(c.index)) ? Number(c.index) : 0;
    const rows = Array.isArray(c.rows) ? c.rows : [];
    const salesWeek = parseWeekFromSalesCategory(cat);
    const sparewayWeek = parseWeekFromSparewayCategory(cat);
    if (salesWeek != null) salesWeekBuckets[salesWeek][idx] = rows;
    else if (sparewayWeek != null) salesSparewayWeekBuckets[sparewayWeek][idx] = rows;
    else if (cat === "sales") salesBuckets[idx] = rows;
    else if (cat === "purchases") purchasesBuckets[idx] = rows;
    else if (cat === "transferIn") transferInBuckets[idx] = rows;
    else if (cat === "transferOutPrev") transferOutPrevBuckets[idx] = rows;
    else if (cat === "transferOutCurr") transferOutCurrBuckets[idx] = rows;
    else if (cat === "salesReturns") srBuckets[idx] = rows;
    else if (cat === "salesSpareway") salesSparewayBuckets[idx] = rows;
    else if (cat === "expenses") expensesBuckets[idx] = rows;
  });

  const salesByWeek = mergeWeekBucketMap(salesWeekBuckets);
  const salesSparewayByWeek = mergeWeekBucketMap(salesSparewayWeekBuckets);
  const legacySales = mergeChunkBuckets(salesBuckets);
  const legacySpareway = mergeChunkBuckets(salesSparewayBuckets);
  const hasWeeklySales = flattenSalesByWeek(salesByWeek).length > 0;
  const hasWeeklySpareway = flattenSalesByWeek(salesSparewayByWeek).length > 0;

  if (!hasWeeklySales && legacySales.length) {
    salesByWeek[1] = legacySales;
  }
  if (!hasWeeklySpareway && legacySpareway.length) {
    salesSparewayByWeek[1] = legacySpareway;
  }

  return {
    sales: hasWeeklySales ? flattenSalesByWeek(salesByWeek) : legacySales,
    salesByWeek,
    purchases: mergeChunkBuckets(purchasesBuckets),
    transferIn: mergeChunkBuckets(transferInBuckets),
    transferOutPrev: mergeChunkBuckets(transferOutPrevBuckets),
    transferOutCurr: mergeChunkBuckets(transferOutCurrBuckets),
    salesReturns: mergeChunkBuckets(srBuckets),
    salesSpareway: hasWeeklySpareway ? flattenSalesByWeek(salesSparewayByWeek) : legacySpareway,
    salesSparewayByWeek,
    expenses: mergeChunkBuckets(expensesBuckets),
  };
}

function pctCh(prev, curr) {
  if (!prev || Number.isNaN(Number(prev))) return 0;
  return ((curr - prev) / Math.abs(prev)) * 100;
}

/**
 * Resolve the brand text for a manual line. Prefer the `mfr` column, but fall
 * back to `group` for rows imported before the Mfr/Group header fix (those
 * stored the brand in `group` because the importer misread the "Mfr." column).
 */
function brandRawFromLine(line) {
  return String((line && (line.mfr || line.group)) || "").trim();
}

/** Manual overrides in Firestore are often saved with grouping commas (e.g. "3,724,517.26"). */
export function parseOverrideAmount(v) {
  if (v == null) return null;
  const s = String(v).trim();
  if (!s) return null;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Parse Value column from a manual upload line (handles comma-grouped strings). */
export function lineAmount(line) {
  if (!line) return 0;
  const raw = line.value;
  if (raw == null || raw === "") return 0;
  const n = Number(String(raw).replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
}

function zeroReportBrandRow(name) {
  return {
    name,
    topSale: 0,
    prevSale: 0,
    currSale: 0,
    salePct: 0,
    purchasePrev: 0,
    purchaseCurr: 0,
    purchasePct: 0,
    oePct: 55 + (MONTHLY_AUTOS_BRANDS.indexOf(name) * 3) % 20,
    otherPct: 45,
    oeAmt: 0,
    otherAmt: 0,
    brandTotal: 0,
  };
}

/** Spareway (Branch 2) current-month sales on the report come from manual uploads, not auto outward. */
function clearAutoSaleBuckets(byName, footer, report) {
  for (const name of MONTHLY_AUTOS_BRANDS) {
    const b = byName[name];
    if (!b) continue;
    b.currSale = 0;
    b.topSale = 0;
    b.oeAmt = 0;
    b.otherAmt = 0;
    b.salePct = 0;
  }
  if (footer) {
    footer.miscSale = 0;
    footer.totalSale = 0;
  }
  if (report?.totals) {
    report.totals.cashSale = 0;
    report.totals.creditBill = 0;
    report.totals.otherSale = 0;
  }
  if (report?.saleSplit) {
    report.saleSplit.oeAmt = 0;
    report.saleSplit.ordinaryAmt = 0;
    report.saleSplit.oePct = 0;
    report.saleSplit.ordinaryPct = 0;
  }
  if (report?.collection) {
    report.collection.cashSale = 0;
    report.collection.saleCollection = 0;
  }
}

/** Branch 3 purchase on the report is driven only by manual uploads (not auto inward invoices). */
function clearAutoPurchaseBuckets(byName, footer) {
  for (const name of MONTHLY_AUTOS_BRANDS) {
    const b = byName[name];
    if (!b) continue;
    b.purchaseCurr = 0;
    b.purchasePrev = 0;
    b.purchasePct = 0;
  }
  if (footer) {
    footer.miscPurchase = 0;
    footer.totalPurchase = 0;
  }
}

function sumManualPurchaseLines(purchaseLines) {
  let total = 0;
  for (const line of purchaseLines || []) {
    total += lineAmount(line);
  }
  return total;
}

export function sumManualSalesLines(salesLines) {
  return sumManualPurchaseLines(salesLines);
}

/** Sum manual upload lines for one brand (Mfr. / Group column). */
function sumManualLinesForBrand(lines, brandName) {
  let total = 0;
  for (const line of lines || []) {
    if (normalizeBrand(brandRawFromLine(line)) === brandName) {
      total += lineAmount(line);
    }
  }
  return total;
}

/** Sum Value column from uploaded transfer IN lines (full file stored in lineChunks). */
export function sumManualTransferInLines(transferInLines) {
  return sumManualPurchaseLines(transferInLines);
}

/** Prefer stored upload lines; fall back to manual override total. */
export function resolveTransferInTotal(manual) {
  const lines = manual?.transferIn;
  if (Array.isArray(lines) && lines.length > 0) {
    return sumManualTransferInLines(lines);
  }
  return parseOverrideAmount(manual?.overrides?.transferIn);
}

/** Sum Value column from uploaded transfer OUT lines (full file in lineChunks). */
export function sumManualTransferOutLines(transferOutLines) {
  return sumManualPurchaseLines(transferOutLines);
}

export function resolveTransferOutPrevTotal(manual) {
  const lines = manual?.transferOutPrev;
  if (Array.isArray(lines) && lines.length > 0) {
    return sumManualTransferOutLines(lines);
  }
  return parseOverrideAmount(manual?.overrides?.transferOutPrev);
}

export function resolveTransferOutCurrTotal(manual) {
  const lines = manual?.transferOutCurr;
  if (Array.isArray(lines) && lines.length > 0) {
    return sumManualTransferOutLines(lines);
  }
  return parseOverrideAmount(manual?.overrides?.transferOutCurr);
}

/** Ensure every brand bucket exists so manual purchase/sale lines always have a target. */
function ensureBrandMap(report) {
  const byName = {};
  for (const b of report.brands || []) {
    if (b?.name) byName[b.name] = b;
  }
  for (const name of MONTHLY_AUTOS_BRANDS) {
    if (!byName[name]) byName[name] = zeroReportBrandRow(name);
  }
  report.brands = MONTHLY_AUTOS_BRANDS.map((name) => byName[name]);
  return byName;
}

/** Stable slug for Firestore doc id (no spaces). */
export function branchSlugForDoc(branchFilter) {
  const b = normalizeBranchValue(branchFilter);
  if (b === "Branch 2") return "Branch2";
  if (b === "Branch 3") return "Branch3";
  if (b === "Wholesale") return "Wholesale";
  return "Main";
}

/** Pre–per-branch storage: `YYYY-MM` only. Main-branch reads fall back here if no `YYYY-MM_Main` doc. */
export function monthDocIdLegacy(year, monthIndex) {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}`;
}

/**
 * Per-month manual storage id, scoped by branch so each branch has its own uploads/overrides.
 * @param {string} [branchFilter='Main'] — `Main` | `Branch 2` | `Branch 3`
 */
export function monthDocId(year, monthIndex, branchFilter = "Main") {
  const base = monthDocIdLegacy(year, monthIndex);
  return `${base}_${branchSlugForDoc(branchFilter)}`;
}

export function emptyManualBundle() {
  return {
    sales: [],
    salesByWeek: emptySalesByWeek(),
    purchases: [],
    transferIn: [],
    transferOutPrev: [],
    transferOutCurr: [],
    salesReturns: [],
    salesSpareway: [],
    salesSparewayByWeek: emptySalesByWeek(),
    expenses: [],
    overrides: {},
    weeklySalesLog: {},
    weeklySparewayLog: {},
  };
}

export function emptyManualLine() {
  return {
    particulars: "",
    description: "",
    group: "",
    qty: "",
    unit: "",
    avgRate: "",
    value: "",
    frQty: "",
    mfr: "",
  };
}

/** Monthly sale report accepts only indirect / admin expense uploads. */
export function normalizeExpenseCategory() {
  return "indirect";
}

export function buildExpenseBreakdown(expenseLines) {
  const lines = Array.isArray(expenseLines) ? expenseLines : [];
  let expenseDirectTotal = 0;
  let expenseIndirectTotal = 0;
  for (const row of lines) {
    const v = parseIndirectExpenseAmount(row.value);
    if (!Number.isFinite(v) || v === 0) continue;
    if (row.category === "direct") expenseDirectTotal += v;
    else expenseIndirectTotal += v;
  }
  return {
    expenseLines: lines,
    expenseDirectTotal,
    expenseIndirectTotal,
    totalExpense: expenseDirectTotal + expenseIndirectTotal,
  };
}

function hasManualBundleContent(b) {
  if (!b) return false;
  const weeklySalesCount = b.salesByWeek
    ? flattenSalesByWeek(b.salesByWeek).length
    : Array.isArray(b.sales)
      ? b.sales.length
      : 0;
  const weeklySparewayCount = b.salesSparewayByWeek
    ? flattenSalesByWeek(b.salesSparewayByWeek).length
    : Array.isArray(b.salesSpareway)
      ? b.salesSpareway.length
      : 0;
  const nLines =
    weeklySalesCount +
    (Array.isArray(b.purchases) ? b.purchases.length : 0) +
    (Array.isArray(b.transferIn) ? b.transferIn.length : 0) +
    (Array.isArray(b.transferOutPrev) ? b.transferOutPrev.length : 0) +
    (Array.isArray(b.transferOutCurr) ? b.transferOutCurr.length : 0) +
    (Array.isArray(b.salesReturns) ? b.salesReturns.length : 0) +
    weeklySparewayCount +
    (Array.isArray(b.expenses) ? b.expenses.length : 0);
  if (nLines > 0) return true;
  const ov = b.overrides && typeof b.overrides === "object" ? b.overrides : {};
  for (const k of [
    "cashSale",
    "creditBill",
    "otherSale",
    "saleCollection",
    "transferOutPrev",
    "transferOutCurr",
    "transferIn",
    "totalExpense",
  ]) {
    if (ov[k] != null && String(ov[k]).trim() !== "") return true;
  }
  const ts = ov.topSales;
  if (ts && typeof ts === "object") {
    for (const v of Object.values(ts)) {
      if (v != null && String(v).trim() !== "") return true;
    }
  }
  return false;
}

async function loadManualBundleFromSnap(snap) {
  const id = snap.ref.id;
  const d = snap.data() || {};
  const version = Number(d.schemaVersion) || 1;

  if (version >= SCHEMA_VERSION_CHUNKED) {
    const fromChunks = await loadLineChunks(id);
    const fallbackSales = Array.isArray(d.sales) ? d.sales : [];
    const fallbackPurch = Array.isArray(d.purchases) ? d.purchases : [];
    const fallbackTransferIn = Array.isArray(d.transferIn) ? d.transferIn : [];
    const fallbackTransferOutPrev = Array.isArray(d.transferOutPrev) ? d.transferOutPrev : [];
    const fallbackTransferOutCurr = Array.isArray(d.transferOutCurr) ? d.transferOutCurr : [];
    const fallbackSr = Array.isArray(d.salesReturns) ? d.salesReturns : [];
    const fallbackSpareway = Array.isArray(d.salesSpareway) ? d.salesSpareway : [];
    const fallbackExpenses = Array.isArray(d.expenses) ? d.expenses : [];
    return {
      sales: fromChunks.sales.length ? fromChunks.sales : fallbackSales,
      salesByWeek: fromChunks.salesByWeek || emptySalesByWeek(),
      purchases: fromChunks.purchases.length ? fromChunks.purchases : fallbackPurch,
      transferIn: fromChunks.transferIn.length ? fromChunks.transferIn : fallbackTransferIn,
      transferOutPrev: fromChunks.transferOutPrev.length
        ? fromChunks.transferOutPrev
        : fallbackTransferOutPrev,
      transferOutCurr: fromChunks.transferOutCurr.length
        ? fromChunks.transferOutCurr
        : fallbackTransferOutCurr,
      salesReturns: fromChunks.salesReturns.length ? fromChunks.salesReturns : fallbackSr,
      salesSpareway: fromChunks.salesSpareway.length ? fromChunks.salesSpareway : fallbackSpareway,
      salesSparewayByWeek: fromChunks.salesSparewayByWeek || emptySalesByWeek(),
      expenses: fromChunks.expenses.length ? fromChunks.expenses : fallbackExpenses,
      overrides: d.overrides && typeof d.overrides === "object" ? d.overrides : {},
      weeklySalesLog:
        d.weeklySalesLog && typeof d.weeklySalesLog === "object" ? d.weeklySalesLog : {},
      weeklySparewayLog:
        d.weeklySparewayLog && typeof d.weeklySparewayLog === "object" ? d.weeklySparewayLog : {},
    };
  }

  return {
    sales: Array.isArray(d.sales) ? d.sales : [],
    salesByWeek: emptySalesByWeek(),
    purchases: Array.isArray(d.purchases) ? d.purchases : [],
    transferIn: Array.isArray(d.transferIn) ? d.transferIn : [],
    transferOutPrev: Array.isArray(d.transferOutPrev) ? d.transferOutPrev : [],
    transferOutCurr: Array.isArray(d.transferOutCurr) ? d.transferOutCurr : [],
    salesReturns: Array.isArray(d.salesReturns) ? d.salesReturns : [],
    salesSpareway: Array.isArray(d.salesSpareway) ? d.salesSpareway : [],
    salesSparewayByWeek: emptySalesByWeek(),
    expenses: Array.isArray(d.expenses) ? d.expenses : [],
    overrides: d.overrides && typeof d.overrides === "object" ? d.overrides : {},
    weeklySalesLog: {},
    weeklySparewayLog: {},
  };
}

export async function loadMonthlyManualEntries(year, monthIndex, branchFilter = "Main") {
  const primary = monthDocId(year, monthIndex, branchFilter);
  const legacyId =
    branchSlugForDoc(branchFilter) === "Main" && monthDocIdLegacy(year, monthIndex) !== primary
      ? monthDocIdLegacy(year, monthIndex)
      : null;

  const primarySnap = await getDoc(doc(db, COLLECTION, primary));

  if (!primarySnap.exists()) {
    if (!legacyId) {
      return emptyManualBundle();
    }
    const legacySnap = await getDoc(doc(db, COLLECTION, legacyId));
    if (!legacySnap.exists()) {
      return emptyManualBundle();
    }
    return loadManualBundleFromSnap(legacySnap);
  }

  const primaryBundle = await loadManualBundleFromSnap(primarySnap);
  if (legacyId && !hasManualBundleContent(primaryBundle)) {
    const legacySnap = await getDoc(doc(db, COLLECTION, legacyId));
    if (legacySnap.exists()) {
      const legacyBundle = await loadManualBundleFromSnap(legacySnap);
      if (hasManualBundleContent(legacyBundle)) {
        return legacyBundle;
      }
    }
  }

  return primaryBundle;
}

export async function loadLatestTopSales(year, monthIndex, lookbackMonths = 120, branchFilter = "Main") {
  const BATCH = 12;
  for (let batchStart = 0; batchStart <= lookbackMonths; batchStart += BATCH) {
    const tasks = [];
    for (let k = 0; k < BATCH && batchStart + k <= lookbackMonths; k += 1) {
      const i = batchStart + k;
      const d = new Date(year, monthIndex - i, 1);
      tasks.push(
        loadMonthlyManualEntries(d.getFullYear(), d.getMonth(), branchFilter).then((manual) => ({
          i,
          manual,
        }))
      );
    }
    const results = await Promise.all(tasks);
    results.sort((a, b) => a.i - b.i);
    for (const { manual } of results) {
      const ts =
        manual?.overrides?.topSales && typeof manual.overrides.topSales === "object"
          ? manual.overrides.topSales
          : null;
      if (ts && Object.keys(ts).length > 0) return ts;
    }
  }
  return {};
}

export async function saveMonthlyManualEntries(year, monthIndex, bundle, branchFilter = "Main") {
  const id = monthDocId(year, monthIndex, branchFilter);
  const ref = doc(db, COLLECTION, id);

  const salesByWeek =
    bundle.salesByWeek && typeof bundle.salesByWeek === "object"
      ? bundle.salesByWeek
      : null;
  const sales = salesByWeek
    ? flattenSalesByWeek(salesByWeek)
    : Array.isArray(bundle.sales)
      ? bundle.sales
      : [];
  const salesSparewayByWeek =
    bundle.salesSparewayByWeek && typeof bundle.salesSparewayByWeek === "object"
      ? bundle.salesSparewayByWeek
      : null;
  const salesSpareway = salesSparewayByWeek
    ? flattenSalesByWeek(salesSparewayByWeek)
    : Array.isArray(bundle.salesSpareway)
      ? bundle.salesSpareway
      : [];
  const purchases = Array.isArray(bundle.purchases) ? bundle.purchases : [];
  const transferIn = Array.isArray(bundle.transferIn) ? bundle.transferIn : [];
  const transferOutPrev = Array.isArray(bundle.transferOutPrev) ? bundle.transferOutPrev : [];
  const transferOutCurr = Array.isArray(bundle.transferOutCurr) ? bundle.transferOutCurr : [];
  const salesReturns = Array.isArray(bundle.salesReturns) ? bundle.salesReturns : [];
  const expenses = Array.isArray(bundle.expenses) ? bundle.expenses : [];

  await deleteAllLineChunks(id);

  const purchasesChunks = chunkRowsByApproxBytes(purchases);
  const transferInChunks = chunkRowsByApproxBytes(transferIn);
  const transferOutPrevChunks = chunkRowsByApproxBytes(transferOutPrev);
  const transferOutCurrChunks = chunkRowsByApproxBytes(transferOutCurr);
  const salesReturnsChunks = chunkRowsByApproxBytes(salesReturns);
  const expensesChunks = chunkRowsByApproxBytes(expenses);

  const chunkWrites = [];
  if (salesByWeek) {
    for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
      const weekRows = Array.isArray(salesByWeek[w]) ? salesByWeek[w] : [];
      const weekChunks = chunkRowsByApproxBytes(weekRows);
      const cat = salesWeekCategory(w);
      for (let i = 0; i < weekChunks.length; i += 1) {
        chunkWrites.push({
          id: chunkDocId(cat, i),
          data: { category: cat, index: i, rows: weekChunks[i] },
        });
      }
    }
  } else {
    const salesChunks = chunkRowsByApproxBytes(sales);
    for (let i = 0; i < salesChunks.length; i += 1) {
      chunkWrites.push({
        id: chunkDocId("sales", i),
        data: { category: "sales", index: i, rows: salesChunks[i] },
      });
    }
  }
  for (let i = 0; i < purchasesChunks.length; i += 1) {
    chunkWrites.push({
      id: chunkDocId("purchases", i),
      data: { category: "purchases", index: i, rows: purchasesChunks[i] },
    });
  }
  for (let i = 0; i < transferInChunks.length; i += 1) {
    chunkWrites.push({
      id: chunkDocId("transferIn", i),
      data: { category: "transferIn", index: i, rows: transferInChunks[i] },
    });
  }
  for (let i = 0; i < transferOutPrevChunks.length; i += 1) {
    chunkWrites.push({
      id: chunkDocId("transferOutPrev", i),
      data: { category: "transferOutPrev", index: i, rows: transferOutPrevChunks[i] },
    });
  }
  for (let i = 0; i < transferOutCurrChunks.length; i += 1) {
    chunkWrites.push({
      id: chunkDocId("transferOutCurr", i),
      data: { category: "transferOutCurr", index: i, rows: transferOutCurrChunks[i] },
    });
  }
  for (let i = 0; i < salesReturnsChunks.length; i += 1) {
    chunkWrites.push({
      id: chunkDocId("salesReturns", i),
      data: { category: "salesReturns", index: i, rows: salesReturnsChunks[i] },
    });
  }
  if (salesSparewayByWeek) {
    for (let w = 1; w <= SALES_WEEKS_PER_MONTH; w += 1) {
      const weekRows = Array.isArray(salesSparewayByWeek[w]) ? salesSparewayByWeek[w] : [];
      const weekChunks = chunkRowsByApproxBytes(weekRows);
      const cat = salesSparewayWeekCategory(w);
      for (let i = 0; i < weekChunks.length; i += 1) {
        chunkWrites.push({
          id: chunkDocId(cat, i),
          data: { category: cat, index: i, rows: weekChunks[i] },
        });
      }
    }
  } else {
    const salesSparewayChunks = chunkRowsByApproxBytes(salesSpareway);
    for (let i = 0; i < salesSparewayChunks.length; i += 1) {
      chunkWrites.push({
        id: chunkDocId("salesSpareway", i),
        data: { category: "salesSpareway", index: i, rows: salesSparewayChunks[i] },
      });
    }
  }
  for (let i = 0; i < expensesChunks.length; i += 1) {
    chunkWrites.push({
      id: chunkDocId("expenses", i),
      data: { category: "expenses", index: i, rows: expensesChunks[i] },
    });
  }

  for (let i = 0; i < chunkWrites.length; i += 400) {
    const batch = writeBatch(db);
    for (const w of chunkWrites.slice(i, i + 400)) {
      batch.set(doc(db, COLLECTION, id, LINE_CHUNKS_SUB, w.id), w.data);
    }
    await batch.commit();
  }

  const rootPayload = {
    schemaVersion: SCHEMA_VERSION_CHUNKED,
    overrides: bundle.overrides && typeof bundle.overrides === "object" ? bundle.overrides : {},
    updatedAt: serverTimestamp(),
    sales: deleteField(),
    purchases: deleteField(),
    transferIn: deleteField(),
    transferOutPrev: deleteField(),
    transferOutCurr: deleteField(),
    salesReturns: deleteField(),
    salesSpareway: deleteField(),
    expenses: deleteField(),
  };
  if (bundle.weeklySalesLog && typeof bundle.weeklySalesLog === "object") {
    rootPayload.weeklySalesLog = bundle.weeklySalesLog;
  }
  if (bundle.weeklySparewayLog && typeof bundle.weeklySparewayLog === "object") {
    rootPayload.weeklySparewayLog = bundle.weeklySparewayLog;
  }
  await setDoc(ref, rootPayload, { merge: true });
}

export async function deleteMonthlyManualEntries(year, monthIndex, branchFilter = "Main") {
  const id = monthDocId(year, monthIndex, branchFilter);
  await deleteAllLineChunks(id);
  const ref = doc(db, COLLECTION, id);
  await deleteDoc(ref);
}

export function mergeMonthlyManualIntoReport(report, manual, branchFilter = "Main") {
  if (!report) return null;
  const m = manual || {
    sales: [],
    purchases: [],
    transferIn: [],
    salesReturns: [],
    salesSpareway: [],
    expenses: [],
  };
  const R = JSON.parse(JSON.stringify(report));
  const byName = ensureBrandMap(R);

  const sr = { ...(R.saleReturnByBrand || {}) };
  R.saleReturnByBrand = sr;

  if (branchSlugForDoc(branchFilter) === "Branch2") {
    clearAutoSaleBuckets(byName, R.footer, R);
  }

  const applyManualSaleLine = (line, sign) => {
    const v = lineAmount(line) * sign;
    if (!v) return;
    const raw = brandRawFromLine(line);
    const brandKey = normalizeBrand(raw);
    const oeB = oeBrandFromBrandField(raw);
    if (brandKey === "MISCELLANEOUS") {
      R.footer.miscSale = (R.footer.miscSale || 0) + v;
    } else if (byName[brandKey]) {
      byName[brandKey].currSale += v;
      byName[brandKey].topSale += v;
      if (oeB) {
        byName[brandKey].oeAmt += v;
        R.saleSplit.oeAmt += v;
      } else {
        byName[brandKey].otherAmt += v;
        R.saleSplit.ordinaryAmt += v;
      }
      const sub = byName[brandKey].currSale;
      const oea = byName[brandKey].oeAmt;
      const otha = byName[brandKey].otherAmt;
      byName[brandKey].oePct = sub > 0 ? (oea / sub) * 100 : byName[brandKey].oePct;
      byName[brandKey].otherPct = sub > 0 ? (otha / sub) * 100 : byName[brandKey].otherPct;
      byName[brandKey].salePct = pctCh(byName[brandKey].prevSale, byName[brandKey].currSale);
    }
    R.footer.totalSale = (R.footer.totalSale || 0) + v;
  };

  for (const line of m.sales || []) {
    applyManualSaleLine(line, 1);
  }

  if (branchSlugForDoc(branchFilter) === "Main") {
    for (const line of m.salesSpareway || []) {
      applyManualSaleLine(line, -1);
    }
  }

  // Keep Avg. Day Sale (current month) in sync with manual sales uploads.
  const currWorkingDays = Math.max(1, Number(R.workingDays?.curr) || 1);
  const currTotalSaleForAvg = Number(R.footer?.totalSale || 0);
  if (R.avgDaySale) {
    R.avgDaySale.curr = currTotalSaleForAvg / currWorkingDays;
    R.avgDaySale.pctChange = pctCh(Number(R.avgDaySale.prev || 0), Number(R.avgDaySale.curr || 0));
  }

  const totSplit = (R.saleSplit.oeAmt || 0) + (R.saleSplit.ordinaryAmt || 0);
  if (totSplit > 0) {
    R.saleSplit.oePct = Math.round((R.saleSplit.oeAmt / totSplit) * 10000) / 100;
    R.saleSplit.ordinaryPct =
      Math.round((R.saleSplit.ordinaryAmt / totSplit) * 10000) / 100;
  }

  const isBranch3 = branchSlugForDoc(branchFilter) === "Branch3";
  if (isBranch3) {
    clearAutoPurchaseBuckets(byName, R.footer);
  }

  for (const line of m.purchases || []) {
    const v = lineAmount(line);
    if (!v) continue;
    const raw = brandRawFromLine(line);
    const brandKey = normalizeBrand(raw);
    if (brandKey === "MISCELLANEOUS") {
      R.footer.miscPurchase = (R.footer.miscPurchase || 0) + v;
    } else if (byName[brandKey]) {
      byName[brandKey].purchaseCurr += v;
      byName[brandKey].purchasePct = pctCh(
        byName[brandKey].purchasePrev,
        byName[brandKey].purchaseCurr
      );
    }
    R.footer.totalPurchase = (R.footer.totalPurchase || 0) + v;
  }

  const srOe = {};
  const srOrd = {};
  for (const line of m.salesReturns || []) {
    const v = lineAmount(line);
    if (!v) continue;
    const raw = brandRawFromLine(line);
    const brandKey = normalizeBrand(raw);
    const oeB = oeBrandFromBrandField(raw);
    if (brandKey !== "MISCELLANEOUS" && MONTHLY_AUTOS_BRANDS.includes(brandKey)) {
      if (oeB) {
        srOe[brandKey] = (srOe[brandKey] || 0) + v;
      } else {
        srOrd[brandKey] = (srOrd[brandKey] || 0) + v;
      }
      sr[brandKey] = (sr[brandKey] || 0) + v;
    }
    R.collection.lessSaleReturn = (R.collection.lessSaleReturn || 0) + v;
    if (R.totals) R.totals.lessSR = (R.totals.lessSR || 0) + v;
  }

  R.saleReturnOeByBrand = { ...(R.saleReturnOeByBrand || {}), ...srOe };
  R.saleReturnOrdByBrand = { ...(R.saleReturnOrdByBrand || {}), ...srOrd };

  const expenseLines = [];
  for (const line of m.expenses || []) {
    const v = parseIndirectExpenseAmount(line.value);
    if (!Number.isFinite(v) || v === 0) continue;
    const particulars = String(line.particulars || line.description || "").trim() || "—";
    expenseLines.push({ particulars, value: v, category: "indirect" });
  }
  expenseLines.sort((a, b) => {
    if (a.category !== b.category) return a.category === "direct" ? -1 : 1;
    return a.particulars.localeCompare(b.particulars, undefined, { sensitivity: "base" });
  });
  const expenseBreakdown = buildExpenseBreakdown(expenseLines);
  R.footer.expenseLines = expenseBreakdown.expenseLines;
  R.footer.expenseDirectTotal = expenseBreakdown.expenseDirectTotal;
  R.footer.expenseIndirectTotal = expenseBreakdown.expenseIndirectTotal;
  R.footer.totalExpense = expenseBreakdown.totalExpense;

  R.brands = MONTHLY_AUTOS_BRANDS.map((name) => byName[name]);

  const tcih =
    (R.collection.cashSale || 0) +
    (R.collection.saleCollection || 0) -
    (R.collection.lessCashDiscount || 0) -
    (R.collection.lessSaleReturn || 0);
  R.collection.totalCashInHandParens = Math.abs(tcih);

  return R;
}

/**
 * Applies manual upload of previous month to mapped page data, so "Previous Month"
 * columns (sale/purchase) reflect manual data uploaded for that prior period.
 * For Main branch only, `salesSpareway` lines are subtracted from previous-month brand
 * sale and from the manual portion of avg. day sale — same rule as current month.
 *
 * @param {string} [branchFilter='Main']
 */
export function mergePreviousMonthManualIntoSalePageData(
  mappedData,
  prevManual,
  branchFilter = "Main"
) {
  if (!mappedData) return null;
  const m = prevManual || {
    sales: [],
    purchases: [],
    transferIn: [],
    salesReturns: [],
    salesSpareway: [],
  };
  const next = {
    ...mappedData,
    brands: Array.isArray(mappedData.brands) ? mappedData.brands.map((b) => ({ ...b })) : [],
  };
  const byName = {};
  for (const b of next.brands) byName[b.n] = b;

  const isBranch3 = branchSlugForDoc(branchFilter) === "Branch3";
  if (isBranch3) {
    for (const b of next.brands) {
      b.pp = 0;
    }
    next.miscPurchase = 0;
  }

  for (const line of m.sales || []) {
    const v = lineAmount(line);
    if (!v) continue;
    const brand = normalizeBrand(brandRawFromLine(line));
    if (brand !== "MISCELLANEOUS" && byName[brand]) {
      byName[brand].p = (Number(byName[brand].p) || 0) + v;
    }
  }

  if (branchSlugForDoc(branchFilter) === "Main") {
    for (const line of m.salesSpareway || []) {
      const v = lineAmount(line);
      if (!v) continue;
      const brand = normalizeBrand(brandRawFromLine(line));
      if (brand !== "MISCELLANEOUS" && byName[brand]) {
        byName[brand].p = (Number(byName[brand].p) || 0) - v;
      }
    }
  }

  let prevManualSalesTotal = 0;
  for (const line of m.sales || []) {
    prevManualSalesTotal += lineAmount(line);
  }
  if (branchSlugForDoc(branchFilter) === "Main") {
    for (const line of m.salesSpareway || []) {
      prevManualSalesTotal -= lineAmount(line);
    }
  }

  if (isBranch3) {
    for (const brandName of MONTHLY_AUTOS_BRANDS) {
      const row = byName[brandName];
      if (!row) continue;
      row.pp =
        sumManualLinesForBrand(m.purchases, brandName) +
        sumManualLinesForBrand(m.transferIn, brandName);
    }
    next.miscPurchase =
      sumManualLinesForBrand(m.purchases, "MISCELLANEOUS") +
      sumManualLinesForBrand(m.transferIn, "MISCELLANEOUS");
  } else {
    for (const line of m.purchases || []) {
      const v = lineAmount(line);
      if (!v) continue;
      const brand = normalizeBrand(brandRawFromLine(line));
      if (brand !== "MISCELLANEOUS" && byName[brand]) {
        byName[brand].pp = (Number(byName[brand].pp) || 0) + v;
      }
    }
  }

  for (const b of next.brands) {
    const p = Number(b.p) || 0;
    const c = Number(b.c) || 0;
    b.pct = p ? ((c - p) / Math.abs(p)) * 100 : 0;
  }

  const wdPrev = Math.max(1, Number(next.workingDaysPrev) || 1);
  const wdCurr = Math.max(1, Number(next.workingDaysCurr) || 1);
  next.avgPrev = (Number(next.avgPrev) || 0) + prevManualSalesTotal / wdPrev;
  const currAvg = Number(next.avgCurr) || 0;
  next.avgCurr = currAvg;
  next.avgPct = next.avgPrev
    ? ((currAvg - next.avgPrev) / Math.abs(next.avgPrev)) * 100
    : 0;

  return next;
}

export function mergeCurrentMonthOverridesIntoSalePageData(
  mappedData,
  manual,
  prevManual,
  fixedTopSales,
  branchFilter = "Main"
) {
  if (!mappedData) return null;
  const ov = manual?.overrides && typeof manual.overrides === "object" ? manual.overrides : {};
  const pov =
    prevManual?.overrides && typeof prevManual.overrides === "object"
      ? prevManual.overrides
      : {};
  const next = {
    ...mappedData,
    brands: Array.isArray(mappedData.brands) ? mappedData.brands.map((b) => ({ ...b })) : [],
  };

  const cash = parseOverrideAmount(ov.cashSale);
  const credit = parseOverrideAmount(ov.creditBill);
  const other = parseOverrideAmount(ov.otherSale);
  const collection = parseOverrideAmount(ov.saleCollection);
  const transferOutPrevOverride = parseOverrideAmount(ov.transferOutPrev);
  const transferOutCurrOverride = parseOverrideAmount(ov.transferOutCurr);
  const transferOutPrevFromLines = resolveTransferOutPrevTotal(manual);
  const transferOutCurrFromLines = resolveTransferOutCurrTotal(manual);
  const transferInOverride = parseOverrideAmount(ov.transferIn);
  const transferInFromLines = resolveTransferInTotal(manual);
  if (cash != null) next.cashSale = cash;
  if (credit != null) next.creditBill = credit;
  if (other != null) next.otherSale = other;
  if (collection != null) next.saleCollection = collection;
  if (transferOutPrevFromLines != null) next.transferOutPrev = transferOutPrevFromLines;
  else if (transferOutPrevOverride != null) next.transferOutPrev = transferOutPrevOverride;
  if (transferOutCurrFromLines != null) next.transferOutCurr = transferOutCurrFromLines;
  else if (transferOutCurrOverride != null) next.transferOutCurr = transferOutCurrOverride;
  if (transferInFromLines != null) next.transferIn = transferInFromLines;
  else if (transferInOverride != null) next.transferIn = transferInOverride;

  next.subtotal = (Number(next.cashSale) || 0) + (Number(next.saleCollection) || 0) + (Number(next.otherSale) || 0);
  next.totalSale = (Number(next.cashSale) || 0) + (Number(next.creditBill) || 0) + (Number(next.otherSale) || 0);
  if (branchSlugForDoc(branchFilter) === "Branch2") {
    const manualSales = manual?.sales;
    if (Array.isArray(manualSales) && manualSales.length > 0) {
      next.totalSale = sumManualSalesLines(manualSales);
    }
  }
  next.totalCashInHand =
    (Number(next.cashSale) || 0) +
    (Number(next.saleCollection) || 0) -
    (Number(next.lessSaleReturn) || 0);

  const topSalesCurrent = ov.topSales && typeof ov.topSales === "object" ? ov.topSales : {};
  const topSalesPrev = pov.topSales && typeof pov.topSales === "object" ? pov.topSales : {};
  const topSalesFixed = fixedTopSales && typeof fixedTopSales === "object" ? fixedTopSales : {};
  const topSales = { ...topSalesFixed, ...topSalesPrev, ...topSalesCurrent };
  const byName = {};
  for (const b of next.brands) byName[b.n] = b;
  for (const [brand, value] of Object.entries(topSales)) {
    const n = parseOverrideAmount(value);
    if (n != null && byName[brand]) byName[brand].top = n;
  }

  // TOTAL PURCHASE footer row:
  // Main / Spareway — auto + manual purchase minus transfer OUT (current month).
  // Branch 3 — manual purchase upload total + transfer IN only (no auto inward).
  const branchTransferOutCurr = Number(next.transferOutCurr) || 0;
  const branchTransferInCurr = Number(next.transferIn) || 0;
  if (branchSlugForDoc(branchFilter) === "Branch3") {
    const manualPurchaseTotal = sumManualPurchaseLines(manual?.purchases);
    const transferInTotal =
      resolveTransferInTotal(manual) ?? branchTransferInCurr;
    next.totalPurchase = manualPurchaseTotal + transferInTotal;
    // Branch 3: each brand PURCHASE current month = that brand's purchase + transfer IN lines only.
    for (const brandName of MONTHLY_AUTOS_BRANDS) {
      const row = byName[brandName];
      if (!row) continue;
      row.pc =
        sumManualLinesForBrand(manual?.purchases, brandName) +
        sumManualLinesForBrand(manual?.transferIn, brandName);
    }
    next.miscPurchase =
      sumManualLinesForBrand(manual?.purchases, "MISCELLANEOUS") +
      sumManualLinesForBrand(manual?.transferIn, "MISCELLANEOUS");
  } else {
    const grossPurchase = Number(next.totalPurchase) || 0;
    next.totalPurchase = grossPurchase - branchTransferOutCurr;
  }

  const expenseOverride = parseOverrideAmount(ov.totalExpense);
  if (expenseOverride != null) {
    next.totalExpense = expenseOverride;
  } else if (Array.isArray(next.expenseLines) && next.expenseLines.length) {
    const expenseBreakdown = buildExpenseBreakdown(next.expenseLines);
    next.expenseLines = expenseBreakdown.expenseLines;
    next.expenseDirectTotal = expenseBreakdown.expenseDirectTotal;
    next.expenseIndirectTotal = expenseBreakdown.expenseIndirectTotal;
    next.totalExpense = expenseBreakdown.totalExpense;
  }

  return next;
}
